/**
 * Calculs PURS des chiffres-clés du site (aucune base, aucun cache Next) :
 * testés par scripts/tests/chiffres-cles.test.ts. Le module à utiliser dans
 * les pages est src/lib/site/chiffres-cles.ts, qui documente les sources.
 */
import { AGRI_TYPES, FORESTRY_TYPES } from '../../data/project-types'

/** Clé de rapprochement d'un nom de région : sans accents, casse ni tirets (voir src/data/projects.ts). */
export const cleRegion = (r: string): string =>
  r.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[\s\u00a0\u202f-]+/g, ' ').trim()

/** Ce que le calcul lit d'un projet du catalogue. */
export interface ProjetChiffre {
  type: string
  methode?: string | null
  region?: string | null
  codeDepartement?: string | null
  surface?: number | null
  co2Total?: number | null
  nbArbres?: number | null
  dateLabellisation?: string | null
}

export interface ChiffresFamille {
  projets: number
  hectares: number
  /** tCO₂ attendues (séquestration pour la forêt, réductions d'émissions pour l'agriculture). */
  tco2: number
}

export interface ChiffresProjets {
  projets: number
  /** Hectares, arrondis à l'unité. */
  hectares: number
  /** tCO₂ de réductions d'émissions attendues (somme des `co2Total`), arrondies à l'unité. */
  tco2Attendues: number
  arbres: number
  regions: number
  departements: number
  /** Méthodes du Label Bas-Carbone représentées (champ `type`). */
  methodes: number
  foret: ChiffresFamille
  agricole: ChiffresFamille
  /** Première et dernière année de labellisation au catalogue. */
  premiereLabellisation: number | null
  derniereLabellisation: number | null
}

const annee = (d?: string | null): number | null => {
  const m = (d ?? '').match(/(19|20)\d{2}/)
  return m ? Number(m[0]) : null
}

function famille(liste: ProjetChiffre[]): ChiffresFamille {
  return {
    projets: liste.length,
    hectares: Math.round(liste.reduce((a, p) => a + (p.surface || 0), 0)),
    tco2: Math.round(liste.reduce((a, p) => a + (p.co2Total || 0), 0)),
  }
}

/** Chiffres d'une liste de projets (le catalogue public par défaut). */
export function calculerChiffresProjets(liste: readonly ProjetChiffre[]): ChiffresProjets {
  const annees = liste.map((p) => annee(p.dateLabellisation)).filter((a): a is number => a != null)
  return {
    projets: liste.length,
    hectares: Math.round(liste.reduce((a, p) => a + (p.surface || 0), 0)),
    tco2Attendues: Math.round(liste.reduce((a, p) => a + (p.co2Total || 0), 0)),
    arbres: liste.reduce((a, p) => a + (p.nbArbres || 0), 0),
    regions: new Set(liste.map((p) => (p.region ? cleRegion(p.region) : '')).filter(Boolean)).size,
    departements: new Set(liste.map((p) => p.codeDepartement).filter(Boolean)).size,
    methodes: new Set(liste.map((p) => p.type).filter(Boolean)).size,
    foret: famille(liste.filter((p) => FORESTRY_TYPES.includes(p.type))),
    agricole: famille(liste.filter((p) => AGRI_TYPES.includes(p.type))),
    premiereLabellisation: annees.length ? Math.min(...annees) : null,
    derniereLabellisation: annees.length ? Math.max(...annees) : null,
  }
}

/* ─────────────── Contributeurs (Payload) ─────────────── */

export interface ContributionChiffre {
  contributor: unknown
  volume?: number | null
  nature?: string | null
  date?: string | null
}

export interface ContributeurChiffre {
  id: string
  kind?: string | null
}

export interface ChiffresContributeurs {
  /** Contributeurs (entreprises, collectivités, associations, particuliers) ayant au moins une contribution. */
  contributeurs: number
  /** Dont entreprises (`kind` = entreprise). */
  entreprises: number
  /** tCO₂ contractualisées sur des projets Label Bas-Carbone (crédits internationaux exclus). */
  tonnesContractualisees: number
  premiereAnnee: number | null
  /** false : la base n'a pas répondu, les chiffres ne doivent pas être affichés. */
  disponible: boolean
}

const idDe = (v: unknown): string =>
  v && typeof v === 'object' ? String((v as { id?: unknown }).id ?? '') : v ? String(v) : ''

export function calculerChiffresContributeurs(
  contributions: readonly ContributionChiffre[],
  contributeurs: readonly ContributeurChiffre[],
): ChiffresContributeurs {
  const avec = new Set(contributions.map((c) => idDe(c.contributor)).filter(Boolean))
  const lbc = contributions.filter((c) => c.nature !== 'international')
  const annees = contributions.map((c) => annee(c.date)).filter((a): a is number => a != null)
  return {
    contributeurs: avec.size,
    entreprises: contributeurs.filter((c) => avec.has(c.id) && c.kind === 'entreprise').length,
    tonnesContractualisees: Math.round(lbc.reduce((a, c) => a + (c.volume || 0), 0)),
    premiereAnnee: annees.length ? Math.min(...annees) : null,
    disponible: true,
  }
}

/* ─────────────── Arrondis honnêtes ─────────────── */

/**
 * « plus de 340 » si la valeur est 341 et le pas 10 ; « 340 » si la valeur
 * est exactement 340 (jamais « plus de 340 » dans ce cas).
 */
export function plusDe(valeur: number, pas: number, locale: 'fr' | 'en' = 'fr'): string {
  const { prefixe, valeur: v } = partiesPlusDe(valeur, pas, locale)
  return `${prefixe}${new Intl.NumberFormat(locale === 'en' ? 'en-US' : 'fr-FR').format(v)}`
}

/**
 * `plusDe` en deux parties, pour un compteur animé qui formate lui-même le
 * nombre (StatsCounter) : { prefixe: 'plus de ', valeur: 600000 } pour
 * 615 570 au pas de 100 000 ; { prefixe: '', valeur: 340 } pour 340 au pas de 10.
 */
export function partiesPlusDe(valeur: number, pas: number, locale: 'fr' | 'en' = 'fr'): { prefixe: string; valeur: number } {
  const v = Math.floor(valeur)
  const arrondi = pas > 0 ? Math.floor(v / pas) * pas : v
  if (arrondi <= 0 || arrondi === v) return { prefixe: '', valeur: v }
  return { prefixe: locale === 'en' ? 'over ' : 'plus de ', valeur: arrondi }
}

/**
 * Compteur public des réductions attendues du portefeuille (pied de page,
 * À propos, pages secteurs, llms.txt) : arrondi à la centaine de milliers
 * inférieure, « plus de 600 000 » plutôt que « 615 570 » (décision de
 * Guillaume du 14/10/2026).
 */
export const PAS_REDUCTIONS_ATTENDUES = 100000
export const reductionsAttendues = (total: number, locale: 'fr' | 'en' = 'fr'): string =>
  plusDe(total, PAS_REDUCTIONS_ATTENDUES, locale)

/** Le même compteur, pour un compteur animé (StatsCounter : `value` et `prefix`). */
export function compteurReductionsAttendues(total: number, locale: 'fr' | 'en' = 'fr'): { value: number; prefix: string } {
  const { prefixe, valeur } = partiesPlusDe(total, PAS_REDUCTIONS_ATTENDUES, locale)
  return { value: valeur, prefix: prefixe }
}

/** Nombre au format de la langue (espaces fines en français). */
export const nombre = (n: number, locale: 'fr' | 'en' = 'fr'): string =>
  new Intl.NumberFormat(locale === 'en' ? 'en-US' : 'fr-FR').format(Math.round(n))

/** Forme courte pour un grand chiffre d'en-tête : « +200 » (valeur 207, pas 10) ou « 340 » (valeur exacte). */
export function plusDeCourt(valeur: number, pas: number, locale: 'fr' | 'en' = 'fr'): string {
  const fmt = new Intl.NumberFormat(locale === 'en' ? 'en-US' : 'fr-FR')
  const v = Math.floor(valeur)
  const arrondi = pas > 0 ? Math.floor(v / pas) * pas : v
  return arrondi <= 0 || arrondi === v ? fmt.format(v) : `+${fmt.format(arrondi)}`
}
